import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import styles from './Messages.module.css'
import { throttle } from 'lodash-es'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'

export interface MessagesProps {
  messageCount: number
  onLoadMore: () => unknown
  welcome: ReactNode
  list: ReactNode
  scrollRef?: RefObject<HTMLDivElement | null>
}

export function Messages({
  messageCount,
  onLoadMore,
  welcome,
  list,
  scrollRef: scrollRefProp,
}: MessagesProps) {
  const fallbackRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = scrollRefProp ?? fallbackRef
  const { tailing, scrollToBottom } = useReverseScroll(
    scrollRef,
    messageCount,
    onLoadMore,
  )
  return (
    <div className={styles.root}>
      <div className={styles.scroller} ref={scrollRef}>
        <div className={styles.filler} />
        <div className={styles.content}>
          {welcome}
          {list}
        </div>
      </div>
      {!tailing && (
        <div className={styles.scrollToBottomContainer}>
          <RawButton
            onPress={() => scrollToBottom()}
            className={styles.scrollToBottomBtn}
          >
            <Icon auto style={{ transform: 'rotate(90deg)' }} name="arrow" />
          </RawButton>
        </div>
      )}
    </div>
  )
}

const TOP_THRESH = 2000
const BOTTOM_THRESH = 25

/**
 * Used for automatically tailing the bottom of the scroll container.
 *
 * @param ref
 * @param messageCount
 * @param onLoadMore
 */
function useReverseScroll(
  ref: RefObject<HTMLElement | null>,
  messageCount: number,
  onLoadMore: () => unknown,
) {
  // State for whether we are tailing.
  const [tailing, setTailing] = useState(true)
  const prevMsgCountRef = useRef(messageCount)
  const scrollTrackHeightRef = useRef(0)
  const shouldLoadMoreRef = useRef(false)
  const scrollToBottom = useCallback(() => {
    if (ref.current) {
      ref.current.scrollTop = ref.current.scrollHeight
    }
  }, [ref])

  // Attach a scroll listener. `messageCount` is a dependency so that the
  // throttled listener restarts whenever messages come in.
  useEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }
    const onScroll = throttle(() => {
      const { scrollTop, offsetHeight, scrollHeight } = node
      if (scrollTop > TOP_THRESH) {
        if (shouldLoadMoreRef.current) {
          shouldLoadMoreRef.current = false
          onLoadMore()
        }
      } else {
        shouldLoadMoreRef.current = true
      }

      // Whenever we scroll, determine whether to tail.
      setTailing(
        offsetHeight === scrollHeight ||
          scrollTop + offsetHeight > scrollHeight - BOTTOM_THRESH,
      )
    }, 200)
    node.addEventListener('scroll', onScroll)
    return () => {
      node.removeEventListener('scroll', onScroll)
    }
  }, [messageCount, onLoadMore, ref])

  // Make sure we stick to the bottom when needed
  // as well as reposition scroll when loading old messages.
  useLayoutEffect(() => {
    const node = ref.current
    if (!node) {
      return
    }

    // When tailing, scroll to the bottom.
    if (tailing) {
      prevMsgCountRef.current = messageCount
      scrollToBottom()
    } else if (prevMsgCountRef.current < messageCount) {
      // If new messages came in and we are NOT tailing, offset the scroll.
      // This avoids jumping when loading more messages.
      node.scrollTop += node.scrollHeight - scrollTrackHeightRef.current
      prevMsgCountRef.current = messageCount
    }
  }, [scrollToBottom, messageCount, tailing, ref])

  // Messages can grow after the count settles, e.g. when a progression card
  // resolves its step, which would leave the tail short of the bottom.
  useEffect(() => {
    const node = ref.current
    if (!node || !tailing) {
      return
    }
    const observer = new ResizeObserver(() => scrollToBottom())
    for (const child of node.children) {
      observer.observe(child)
    }
    return () => observer.disconnect()
  }, [scrollToBottom, tailing, ref])

  // Any time the component rerenders with the same amount of
  // messages, capture it's scroll info. If the message count changes,
  // the above hook will use the previously captured scroll info to
  // reposition.
  useLayoutEffect(() => {
    if (!ref.current) {
      return
    }
    if (prevMsgCountRef.current === messageCount) {
      scrollTrackHeightRef.current = ref.current.scrollHeight
    }
  }, [messageCount, ref])

  return { tailing, scrollToBottom }
}
